import { Cloudinary } from '@cloudinary/url-gen';

export const AGORA_CONTACT = '1300812345';
export const AGORA_BUYER_URL = 'https://buyer.agoralivestock.com.au';

// Canonical brand name for structured data (schema.org). Deliberately a literal
// rather than NEXT_PUBLIC_NAME, which carries env prefixes like `[uat] `/`[dev] `.
export const BRAND_NAME = 'Agora Livestock';

export const SETTINGS = {
  seo: {
    defaultTitle: process.env.NEXT_PUBLIC_NAME,
    defaultDescription:
      'Agora Livestock is an online marketplace for buying and selling cattle, sheep and goats across Australia. Browse livestock listings, compare prices and connect with sellers.',
    defaultImage: '/images/marketplace-social.jpg',
    twitterSite: '@AgoraLivestock',
  },
};

export const DATE_FORMAT = {
  /** `dd/MM/yyyy` */
  long: 'dd/MM/yyyy',
  /** `d/M/yy` */
  short: 'd/M/yy',
  /**`d MMM */
  xShort: 'd MMM',
  /** 1 Jun 2023 */
  listing: 'd LLL yyyy',
  listing_short: 'd LLL',
  /** TUE, 27 JUN */
  stockbook_week: 'EEE, d MMM',
};

export const DATETIME_FORMAT = {
  /** `dd/MM/yyyy HH:mm:ss` */
  long: 'dd/MM/yyyy HH:mm:ss',
  /** `dd/MM/yyyy HH:mm` */
  medium: 'dd/MM/yyyy HH:mm',
  /** `d/M/yy H:mm` */
  short: 'd/M/yy H:mm',
  /** yeah I dunno */
  extraLong: 'h:mmaaa do LLLL yyyy',
  /** TUE, 27 JUN (02:27PM) */
  confirmationTooltip: 'EEE, d MMM, hh:mm a ',
};

export const PATHS = {
  home: '/',
  contacts: {
    home: '/contacts',
  },
  privateGroups: {
    home: '/networks',
  },
  prices: {
    home: '/prices',
    edit: '/prices/edit',
    new: '/prices/edit/new',
  },
  summaryGrids: {
    home: '/summary-grids',
    edit: '/summary-grids/edit',
    new: '/summary-grids/edit/new',
  },
  offers: {
    home: '/offers',
    edit: '/offers/edit',
    new: '/offers/edit/new',
  },
  stockbook: {
    home: '/stockbook',
    contract: '/stockbook/contract',
    orgConfirmation: '/booking/org-confirmation/',
    orgBooking: '/booking/org-booking/',
    publicConfirmation: '/booking/confirmation/',
    bookingHistory: '/booking/history/',
    orgBookingRequest: '/booking/org-booking-request/',
  },
  picManager: {
    home: '/pic-manager',
  },
  stockRequired: {
    home: '/stockbook-required',
  },

  help: 'https://www.agoralivestock.com.au/faqs',
  auth: {
    logIn: '/auth/login',
    error: '/auth/error',
    logInVerified: '/auth/login?verified=true',
    logInNotVerified: '/auth/login?verified=false',
    signUp: '/auth/sign-up',
    verification: '/auth/verification',
    forgotPassword: '/auth/forgot-password',
    forgotPasswordConfirmation: '/auth/forgot-password-confirmation',
    resetPasswordConfirmation: '/auth/reset-password-confirmation',
  },
  api: {
    invalidateRefreshToken: '/api/auth/invalidate-refresh-token',
  },
  organisation: {
    picker: '/organisation',
    create: '/organisation/create',
    profile: '/organisation/profile',
  },
  profile: { home: '/profile' },
  marketplace: {
    listings: '/marketplace',
    buyListings: '/marketplace/my-buy-listings',
    sellListings: '/marketplace/my-sell-listings',
    publicListings: '/marketplace/listing',
    // NB: this mirrors the page file's route so `public-route.tsx`'s
    // `route.includes(router.pathname)` guard keeps matching (router.pathname
    // is `/marketplace/listing/[id]/[[...slug]]`). Keep in sync with the file.
    publicListing: '/marketplace/listing/[id]/[[...slug]].tsx',
    viewListings: '/marketplace/listing/view',
    createListing: '/marketplace/listing/v2/edit/new',
    editListing: '/marketplace/listing/v2/edit/',
    listingConfirmation: '/marketplace/listing/listings-confirmation',
    alert: '/marketplace?alertModal=true',
    listing: '/marketplace/listing/',
    listingAnalytics: '/marketplace/listing/analytics/',
  },
  media: {
    home: '/media-library',
  },
  app: {
    marketplace: '/app/marketplace',
    alerts: '/app/marketplace-alerts',
    mySellListings: '/app/marketplace/my-sell-listings',
    myBuyListings: '/app/marketplace/my-buy-listings',
    listing: '/app/marketplace/listing',
    viewListing: '/app/marketplace/listing/view',
    addContact: '/app/contacts/add-contact',
    listingAnalytics: '/app/marketplace/listing/analytics/',
  },
};

// Paths that are deemed safe to visit when a buyer org has not yet been selected
export const SAFE_PATHS_NO_ORG = [
  PATHS.profile.home,
  PATHS.organisation.picker,
  PATHS.organisation.create,
  PATHS.marketplace.listings,
  PATHS.marketplace.publicListing,
];

// It's actually 15 minutes but we're refreshing more frequently to ensure the token is always up to date
export const TOKEN_EXPIRY_MINUTES = 5;

export const HEADER_OVERRIDE_PORTAL_ID = 'headerOverrideTarget';

export const PRICE_GRID_EDIT_SECTION_TITLES = {
  livestockType: 'Type of livestock',
  priceAndDelivery: 'Price & delivery information',
  conditions: 'Conditions of purchase',
  livestockSpecs: 'Livestock specifications',
  priceTable: 'Price grid',
};

export const OFFERS_EDIT_SECTION_TITLES = {
  details: 'Offer details',
  priceReference: 'Price reference',
  comments: 'Offer comments',
  delivery: 'Delivery and offer details',
  deliveryInfo: 'Delivery information',
  notes: 'Internal Notes',
};

export const QUERY_KEYS = {
  addresses: 'addresses',
  apiStatus: 'apiStatus',
  booking: 'booking',
  bookingConfirmation: 'bookingConfirmation',
  bookingConfirmationSMS: 'bookingConfirmationSMS',
  bookingHistory: 'bookingHistory',
  bookingStock: 'bookingStock',
  bookings: 'bookings',
  bookingsConfig: 'bookingsConfig',
  bookingsConfigCounts: 'bookingsConfigCounts',
  config: 'config',
  contact: 'contact',
  contactInviteSearch: 'contactInviteSearch',
  contacts: 'contacts',
  enquiry: 'enquiry',
  enquiryList: 'enquiryList',
  listings: 'listings',
  listingAlert: 'listingAlert',
  listingDetails: 'listingDetails',
  marketplaceConfig: 'marketplaceConfig',
  marketplaceListing: 'marketplaceListing',
  marketplacePublicListing: 'marketplacePublicListing',
  marketplaceOffers: 'marketplaceOffers',
  newOrg: 'newOrg',
  offer: 'offer',
  offers: 'offers',
  org: 'organisation',
  orgConfig: 'orgConfig',
  orgs: 'organisations',
  picAttributeList: 'picAttributeList',
  picAutocomplete: 'picAutocomplete',
  picList: 'picList',
  priceGrid: 'priceGrid',
  priceGridEois: 'priceGridEois',
  priceGridListing: 'priceGridListing',
  priceGridOffers: 'priceGridOffers',
  priceGrids: 'priceGrids',
  privateGroup: 'privateGroup',
  privateGroups: 'privateGroups',
  productGrids: 'productGrids',
  products: 'products',
  subscriptions: 'subscriptions',
  summaryGrid: 'summaryGrid',
  summaryGrids: 'summaryGrids',
  user: 'user',
  users: 'users',
  visibilityContacts: 'visibilityContacts',
  visibilityPrivateGroups: 'visibilityPrivateGroups',
  orgMedia: 'orgMedia',
  orgMediaSearch: 'orgMediaSearch',
  bookingRequest: 'bookingRequest',
  bookingRequestSMS: 'bookingRequestSMS',
  orgBookingRequestIncoming: 'orgBookingRequestIncoming',
  orgBookingRequestOutgoing: 'orgBookingRequestOutgoing',
  bookingRequestBuyerOrg: 'bookingRequestBuyerOrg',
  orgUsers: 'orgUsers',
};

export const BUYER_ORG_STORAGE_KEY = 'activeBuyerOrg';

export const FIRST_LOGIN_STORAGE_KEY = 'firstLogin';
export const REDIRECT_URL = 'redirectURL';
export const NAVBAR = 'navbar';

export const APP = 'app';

export const COMMON_PLAN_FEATURES = [
  'Unlimited contacts',
  'Full price grid creation',
  'Broadcasts',
];

export const PUBLIC_CONTACT_PAGE = 'https://agoralivestock.com.au/contact';
export const PUBLIC_TERMS_PAGE = 'https://agoralivestock.com.au/terms-of-use';

export const UPGRADE_MESSAGES = {
  privateGroups:
    'Networks are groups of contacts you can send messages to, such as price changes or new grids. This lets you keep your suppliers are kept up to date with your prices. Networks could include preferred suppliers, certified suppliers or livestock agents.',
};

export const OFFER_REFETCH_INTERVAL = 10000;

export const STATUS_REFETCH_INTERVAL = 60000;

export const USER_MESSAGE = {
  notesVisiblity: '(Not visible to suppliers)',
};

export const cld = new Cloudinary({
  cloud: {
    cloudName: process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME,
  },
  url: {
    /*
      Drop the `_a=` SDK-telemetry parameter. It encodes which Cloudinary SDK
      built the URL, and `@cloudinary/react` emits a different value for the
      same transformation depending on the code path that produced it (its
      `HtmlImageLayer.update` forgets to forward the React SDK's identity, so
      re-renders fall back to the generic url-gen one). Two spellings of one
      image are two cache keys and two fetches; we gain nothing from the
      telemetry, so it goes.
    */
    analytics: false,
  },
});

/**
 * Cloudinary crops MUST specify an explicit width.
 *
 * A crop such as `fill().aspectRatio('3:2')` with no width outputs at the
 * *source* resolution, so a large seller upload (e.g. 8160x4592) produces a
 * ~31MP derived image, which exceeds Cloudinary's transformation limit and
 * returns a 400 instead of an image. Capping the width bounds the output
 * regardless of how big the original upload is.
 *
 * These are CSS pixel sizes, i.e. 1x, paired with `.delivery(dpr(2))` at the
 * call site to stay sharp on a retina display. Do not pre-double them: dpr
 * already does it, and doubling twice ships a 4x image.
 *
 * Images the browser chooses a width for do not use these. They take a ladder
 * of real device-pixel widths from `lib/cloudinary-image` instead, and must not
 * carry `dpr()` at all. What is left here is the fixed-URL cases: the
 * lightbox's full-screen and thumbnail sources.
 */
export const CLOUDINARY_MAX_WIDTH = {
  /** Lightbox thumbnail strip, rendered at 100px wide (150 leaves headroom). */
  thumbnail: 150,
  /** Lightbox full-screen media, rendered up to 1920px wide. */
  fullScreen: 1920,
};

export const FREE_PLAN_FEATURES = {
  target: 'Get started with the market',
  bestFor: 'Low-frequency users',
  values: [
    { feature: 'Limited saleyard report access' },
    {
      feature: 'Marketplace access & alerts',
    },
    {
      feature: 'Marketplace fees: 5% sheep/goats, 5% cattle',
    },
  ],
};
export const PLANS = [
  {
    plan: 'LITE',
    target: 'All saleyard reports, nationwide',
    bestFor: 'Producers who want fast, reliable market reports',
    values: [
      { feature: 'Everything in Free' },
      {
        feature:
          'Full saleyard report access (as soon as reports are published)',
      },
    ],
  },
  {
    plan: 'BASE',
    target: 'Trade with confidence and lower fees',
    bestFor: 'Active producers selling or buying regularly',
    values: [
      { feature: 'Everything in Lite' },
      {
        feature: 'Buyer price grids',
      },
      {
        feature:
          'Reduced marketplace fees:1% sheep/goats | 0.5% cattle (successful transactions only)',
      },
    ],
  },

  {
    plan: 'STANDARD',
    target: 'Deeper market insight for higher-volume decisions',
    bestFor: 'Larger producers and livestock traders',
    values: [
      { feature: 'Everything in Base' },
      {
        feature: 'Market analysis & insights',
      },
      {
        feature:
          'Trade Desk access & trade opportunities (call or message for in-depth info)',
      },
    ],
  },
  {
    plan: 'AGENT',
    target: 'Market and trade livestock with no marketplace fee',
    bestFor: 'Livestock agents',
    values: [
      { feature: 'Everything in Commercial Producers & Traders' },
      {
        feature: 'Your brand, logo & contact details visible on listings',
      },
      {
        feature: 'Increased listing visibility',
      },
      {
        feature: 'Automatically embed your listings on your own website',
      },
      {
        feature: 'Private & internal agency marketplaces',
      },
      {
        feature: 'No marketplace fees',
      },
    ],
  },
  {
    plan: 'PREMIUM',
    target: 'Feedlots & smaller processors',
    bestFor: 'Large producers, feedlots & traders',
    values: [
      { feature: 'Everything in Commercial Producers & Traders' },
      {
        feature: 'POA listings (stock wanted listings)',
      },
      {
        feature: 'Stockbook – digital booking & confirmation platform',
      },
    ],
  },
  {
    plan: 'ENTERPRISE',
    target: 'Fully integrated buying & booking operations',
    bestFor: 'Large processors & buyers (200k+ sheep or 30k+ cattle annually)',
    values: [
      { feature: 'Everything in Buyer' },
      {
        feature: 'Stockbook for full booking records',
      },
      {
        feature: 'Integration with third-party systems (e.g. Triton)',
      },
      {
        feature: 'Additional integrations available',
      },
    ],
  },
];
